import { mount } from 'svelte';
import { defaultConfig } from 'tailwind-variants';

import '@fontsource-variable/noto-sans';
import '@fontsource-variable/outfit';
import './main.css';

import { TeoCore } from '../../teoCore/TeoCore';

import '../../webComponents/LoginElement.svelte';
import '../../webComponents/TPIDashboardElement.svelte';
import '../../webComponents/ConfigPanelElement.svelte';
import '../../webComponents/AssistantElement.svelte';
import '../../webComponents/ProfileElement.svelte';
import '../../webComponents/TeoTabsElement.svelte';
import '../../webComponents/TeoSideNavElement.svelte';
import '../../webComponents/TeoDialogsElement.svelte';
import '../../webComponents/LoginScreenElement.svelte';
import '../../webComponents/TeoAppElement.svelte';

import { TeoText } from '@/teoCore/text/TeoText';
import type { TeoView } from '@/teoCore/text/TextUi';
import EditorHeadlineComponent from '@/ui/components/editor/EditorHeadlineComponent.svelte';

import { LOG, setLogLevel } from '../../lib/Logger';
import EditorComponent from '../../lib/ui/components/editor/EditorComponent.svelte';
import SideBySideEditorElement from '../../lib/ui/components/SideBySideEditor/SideBySideEditorElement.svelte';

setLogLevel('warn');

defaultConfig.twMerge = false;

async function main() {
  const teoCore = await TeoCore.init({
    elements: [
      'teo-assistant',
      'teo-config-panel',
      'teo-loginscreen',
      'teo-sidenav',
      'teo-profile',
      'teo-tabs',
      'teo-tpi-dashboard',
      'teo-dialogs',
      'teo-side-by-side-editor',
      'teo-app',
    ],
    apiBaseUrl: 'https://demo-api.teo.digital/',
    useInsecureHttp: false,
    evaluationProfile: 3,
    tpiTarget: 90,
  });
  window.teoCore = teoCore;
  window.__TEO__getCore = () => teoCore;

  if (teoCore.user.isLoggedIn) {
    document.body.classList.add('logged-in');
  } else {
    document.body.classList.remove('logged-in');
  }

  teoCore.user.events.addEventListener('logIn', () => {
    document.body.classList.add('logged-in');
  });

  teoCore.user.events.addEventListener('logOut', () => {
    document.body.classList.remove('logged-in');
  });

  mount(EditorComponent, {
    target: document.getElementById('teo-editor')!,
    props: {
      teoCore,
    },
  });

  mount(EditorHeadlineComponent, {
    target: document.getElementById('teo-editor-headline')!,
    props: {
      teoCore,
    },
  });

  // Registers the first active text
  if (teoCore.activeText) registerActiveText(teoCore, teoCore.activeText);

  // Registeres new texts if text change
  teoCore.events.addEventListener('activeTextChanged', (detail: TeoText) => {
    registerActiveText(teoCore, detail);
    // Update view
    switchEditorView(teoCore, teoCore.activeText.state.view, detail);
  });
}

let removeEventListerView: (() => void) | undefined = undefined;

function registerActiveText(teoCore: TeoCore, activeText: TeoText): void {
  // Remove old listener
  removeEventListerView?.();

  // Add new listener
  removeEventListerView = activeText.store.addEventListener('view', (view) => {
    switchEditorView(teoCore, view, activeText);
  });
}

const sideBySideStates = new Map<
  number,
  {
    mountedEditor: SideBySideEditorElement | null;
  }
>();
let currentActiveTextId: number | undefined = undefined;

function switchEditorView(teoCore: TeoCore, value: TeoView, activeText: TeoText): void {
  const three = document.getElementById('editor-with-sidebars');
  const two = document.getElementById('side-by-side-editor');
  if (!three || !two) {
    return;
  }

  const prevState = currentActiveTextId && sideBySideStates.get(currentActiveTextId);
  let state = sideBySideStates.get(activeText.id);
  if (!state) {
    state = {
      mountedEditor: null,
    };
    sideBySideStates.set(activeText.id, state);
  }

  // hide previous editor
  if (prevState && prevState !== state) {
    document
      .getElementById(`sbseditor-${currentActiveTextId}`)
      ?.classList.add('editor-view-hidden');
  }

  // show current editor
  if (value === 'plain-language') {
    three.classList.add('editor-view-hidden');
    two.classList.remove('editor-view-hidden');
    if (!state.mountedEditor) {
      state.mountedEditor = mount(SideBySideEditorElement, {
        target: document.getElementById('teo-side-by-side-editor')!,
        props: {
          teoCore,
          editorId: `sbseditor-${activeText.id}`,
        },
      }) as EditorComponent;
    }
    document.getElementById(`sbseditor-${activeText.id}`)?.classList.remove('editor-view-hidden');
  } else {
    three.classList.remove('editor-view-hidden');
    two.classList.add('editor-view-hidden');
    if (state.mountedEditor) {
      document.getElementById(`sbseditor-${activeText.id}`)?.classList.add('editor-view-hidden');
    }
  }

  currentActiveTextId = activeText.id;
}

main()
  .then(() => LOG.info('TEO APP V3 – mounted'))
  .catch(LOG.error);
